import { List, Lock } from "@mui/icons-material";
import { Box, Stack, Tooltip, Typography } from "@mui/material";
import React from "react";
import { Layout, DeletePanel } from "../../components";
import { withNavBars } from "../../HOCs";
import { AppRoutes } from "../../router/routes";
import { Dashboard } from "./dashboard";
import { useAppDispatch, useAppSelector } from "../../redux";
import { useNavigate } from "react-router-dom";
import { DEFAULT_BOARD_VIEW_PAGINATION } from "../../utils";
import { useLazyGetQuotesQuery, useLazyGetTagGroupsQuery, useLazyGetTagsBasedOnParameterQuery, useUpdateQuoteMutation } from "../../redux/services";
import { useToggleView } from "../../hooks";
import { IBoard, IItem } from "../../components/boardLayout/types";
import { TagInfo } from "../../interfaces";
import { BoardLayout } from "../../components/boardLayout";
import { resetQuoteSlice } from "../../redux/slices/quote";
import { HasAccess } from "../../router/authorization";
import { PERMISSIONS } from "../../router/permission";
import { PublishedIcon } from "../../images/svg/publishedIcon";
import { WIPIcon } from "../../images/svg/WIPIcon";
import { openConfirmationDialog, closeConfirmationDialog } from "../../redux/slices/confirmationDialog";
import { openDialog } from "../../redux/slices/dialog";
import { PublishComponent } from "../materials/dashboard";

const Quotes: React.FC<{ children?: JSX.Element }> = (props) => {

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [getAllQuotes] = useLazyGetQuotesQuery({ refetchOnFocus: true });

    const [getTagGroups, tagGroupResponse] = useLazyGetTagGroupsQuery();
    const [getTags, tagsResponse] = useLazyGetTagsBasedOnParameterQuery();
    const [updateQuote] = useUpdateQuoteMutation();

    const [search, setSearch] = React.useState("");
    const { isBoardView, selectedBoard } = useToggleView("quote");

    const isLoading = tagGroupResponse.isFetching || tagGroupResponse.isLoading;

    const { perm } = useAppSelector(store => store.auth.userDetails);
    const creatable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_CREATE), [perm]);
    const publishable = React.useMemo(() => HasAccess(perm, PERMISSIONS.COMMERCIAL_IEQUOTE_PUBLISH), [perm]);

    const addEditQuote = (id?: string) => {
        dispatch(resetQuoteSlice())
        navigate(AppRoutes.viewQuickQuoteBasicDetail(id ?? "New"))
    };

    const boardTypes = (tagsResponse?.data?.tags ?? []).map((t: any) => ({
        ...t, id: t._id, title: t.tag_name, description: t.tag_desc
    }));

    const refetchTagGroups = () => {
        getTagGroups({ ...DEFAULT_BOARD_VIEW_PAGINATION, scope: "quote" });
    };

    const updatePublishStatus = (row: any, refetch: () => void) => {

        if (!row.quote_report?.processing && !row.quote_report?.attachment_id) {
            dispatch(openConfirmationDialog({ title: "Publish Operation", body: "Please generate the quote report before publishing.", positiveBtn: "Ok", hideNegativeBtn: true, onOk: () => dispatch(closeConfirmationDialog()) }));
            return;
        }

        if (row.quote_report?.processing) {
            dispatch(openConfirmationDialog({ title: "Publish Operation", body: "Quote report is being generated. Please wait for a moment.", positiveBtn: "Ok", hideNegativeBtn: true, onOk: () => dispatch(closeConfirmationDialog()) }));
            return;
        }

        dispatch(
            openConfirmationDialog({
                title: "Publish Operation",
                body: <DeletePanel
                    isMaterial={true}
                    message={`Are you sure you want to publish the quote ${row?.sequence_id}?`}
                    warningMessage={`You will not be able to edit this revision after publishing. You can however create new revisions.`}
                />,
                positiveBtn: "Yes",
                negativeBtn: "Cancel",
                onOk: () => proceedToPublish(row, refetch),
                onNegativeBtn: () => dispatch(closeConfirmationDialog())
            })
        );
    };

    const proceedToPublish = (row: any, refetch: () => void) => {
        dispatch(closeConfirmationDialog());
        dispatch(openDialog({
            title: "",
            body: <PublishComponent {...row} refetch={refetch} object_type="quote" object_id={row?._id} />,
            hidePositiveBtn: true,
            hideNegativeBtn: true,
            enablePadding: false
        }));
    };

    React.useEffect(() => {
        if (isBoardView) {
            refetchTagGroups();
        }
        // eslint-disable-next-line
    }, [isBoardView]);

    React.useEffect(() => {
        if (selectedBoard?.canonical_name && tagGroupResponse.isSuccess) {
            getTags(selectedBoard?.canonical_name);
        }
        // eslint-disable-next-line
    }, [selectedBoard?._id, isLoading]);

    return <Box sx={{ padding: 0.5, height: "100%" }}>
        {isBoardView
            ?
            <BoardLayout
                layoutTitleDetails={{
                    showTitle: false,
                    title: "Quick Quote",
                    breadcrumbs: [
                        { id: crypto.randomUUID(), label: "Commercial Module", path: '' }
                    ],
                    currentPath: "Quick Quote"
                }}
                boardViewDetails={{
                    isLoading,
                    entityType: "quote",
                    boards: boardTypes,
                    onClick: (board: IBoard, item: IItem) => {
                        addEditQuote(item._id);
                    },
                    refetchBoards: refetchTagGroups,
                    getTitle(d: IItem) {
                        return (
                            <Stack direction="row" sx={{
                                alignItems: "center"
                            }}>
                                <Typography
                                    id="view-quote-icon"
                                    variant="caption"
                                    color="textSecondary">
                                    {d.title}
                                </Typography>
                                {d?.user_access ? <Tooltip title={`This ${d.title} is private`}><Lock sx={{ ml: 1, fontSize: "14px", marginTop: '-2px !important' }} color='disabled' fontSize={"small"} /></Tooltip> : <></>}
                                <Tooltip title={d?.released ? "Published" : publishable ? "Work in progress, click to publish the item" : "Not Published"} >
                                    <Stack
                                        id={d?.released ? `published-quote` : `unpublished-quote`}
                                        sx={{
                                            cursor: d?.released ? "default" : "pointer",
                                            marginTop: '-2px !important'
                                        }}
                                    >
                                        {d?.released
                                            ? <PublishedIcon sx={{ width: 24, height: 24 }} />
                                            : <WIPIcon onClick={(e) => {
                                                e.stopPropagation();
                                                if (publishable) {
                                                    updatePublishStatus(d, refetchTagGroups)
                                                }
                                            }} sx={{ width: 24, height: 24 }} />}
                                    </Stack>
                                </Tooltip>
                            </Stack>
                        );
                    },
                    searchDetails: {
                        search,
                        onSearch: setSearch,
                        searchPlaceholder: "Search for Quote"
                    },
                    tagFilterOption: { scope: "quote" },
                    creatable,
                    createBtnName: "Add Quote",
                    onCreateBtn: () => addEditQuote(),
                    fetchBoardItems: async (board: IBoard, page: number, rowsPerPage: number, search: string, filters) => {
                        const { data: boardData = { data: [], totalCount: 0 }, ...rest } = await getAllQuotes({ search: search.trim() ? search : "", page: page - 1, pageSize: rowsPerPage, tag_id: [board.id, ...filters?.tags?.map((t) => t.value) ?? []].join(',') });
                        return { ...boardData, ...rest };
                    },
                    onUpdateItemBoard: async (result, item: any) => {
                        const dropTag = boardTypes.find((b: IBoard) => b?.id === result?.destination?.droppableId);
                        if (dropTag) {
                            const updatedItem = {
                                currency_code: item?.currency_code,
                                date_of_issue: item?.date_of_issue,
                                name: item?.name,
                                valid_till: item?.valid_till,
                                tags_info: [...(item.tags_info ?? []).filter((t: TagInfo) => t.tag_group_canonical_name !== (boardTypes?.[0] as any)?.tag_group_canonical_name), dropTag].map((t) => ({ tag_group_id: t.tag_group_id, tag_id: t.tag_id ?? t._id }))
                            }
                            return await updateQuote({ id: item?._id, update_version: false, payload: updatedItem as any });
                        }
                        return new Promise((resolve, reject) => {
                            reject({ error: "Unable to move the quote" });
                        })
                    },
                }}
            />
            :
            <Layout
                history={[
                    { label: "Commercial Module", onClick: () => navigate(AppRoutes.allQuickQuotes) },
                ]}
                currentPath={"Quick Quote"}
                hideNavBarByDefault={true}
                navBars={[{
                    id: "1",
                    title: "Quick Quote",
                    icon: <List fontSize="small" />,
                    path: AppRoutes.allQuickQuotes
                },]}
                mainPanel={<Dashboard onPublish={updatePublishStatus} />}
                sideNavVariant={"whiteboard"}
            />
        }

    </Box>
}

export default withNavBars(Quotes);